import {
  AlignItemsTypes,
  DashboardState,
  DisplayTypes,
  EProjectType,
  ESdChangeReason,
  IKuikaStyle,
  IStylingPosition,
  InvisibleStylePart,
  TextAlignTypes,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { StaticTableHelper } from "../../../../shared/utility/static-table-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { PaperUnit, convertValue } from "../../header/toolbar-right/utils/unit-conversion";
import StyleLabel from "./style-label/style-label";
import { StylingLockComponent } from "./styling-lock-component";
import { StylingSelector } from "./styling-selector/styling-selector";
import { styleSectionNeedsRender } from "./styling-window-helpers";

export interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleName: string;
  performStyleTagOnClose: any | undefined;
}

export interface IPositionComponentState {
  deleteArguments: IDeleteArguments;
}

export interface IPositionComponentProps {
  form: FormInstance<IKuikaStyle>;
  // KOA-15838: true while a style tag is being edited; drives the blue (token)
  // vs orange (locally changed) label colour independently of the form's section
  // id (which a state switch nulls).
  isEditMode?: boolean;
}

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

class PositionComponent extends PureSdComponent<
  IPositionComponentProps & IWithSDProps & IWithContextProp,
  IPositionComponentState
> {
  styleName: any;

  constructor(props: IPositionComponentProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      deleteArguments: { isDeleteStyleModalVisible: false, deletingStyleName: "", performStyleTagOnClose: undefined }
    };
    this.styleName = React.createRef();
  }

  getCurrentReportUnit = (): PaperUnit => {
    if (this.props.sd.dashboardState !== DashboardState.reportDesigner) {
      return "px";
    }

    const selectedScreenCanvasSize = this.props.sd.project?.screenCanvasSizes?.screens?.find(
      (s) => s.id === this.props.sd.sdId
    );

    return (selectedScreenCanvasSize?.reportPaper?.unit as PaperUnit) || "px";
  };

  convertPxToDisplayUnit = (pxValue: number | undefined, unit: PaperUnit): number | undefined => {
    if (pxValue === undefined || unit === "px") return pxValue;
    return convertValue(pxValue, "px", unit);
  };

  convertDisplayUnitToPx = (displayValue: number | undefined, unit: PaperUnit): number | undefined => {
    if (displayValue === undefined || unit === "px") return displayValue;
    return convertValue(displayValue, unit, "px");
  };

  submitForm = (fieldsValue: IKuikaStyle) => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  new = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.position) {
      return;
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingPositions = {} as IStylingPosition;
    stylingPositions.positionId = Guid.create().toString();

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;
    stylingPositions.name = name;
    stylingPositions.componentType = selectedDIItem?.name;

    stylingPositions.styleType = "custom";

    const formValues = {
      isMarginLocked: fieldsValue.position.isMarginLocked,
      isPaddingLocked: fieldsValue.position.isPaddingLocked,
      marginTop: fieldsValue.position.marginTop,
      marginRight: fieldsValue.position.marginRight,
      marginBottom: fieldsValue.position.marginBottom,
      marginLeft: fieldsValue.position.marginLeft,
      paddingTop: fieldsValue.position.paddingTop,
      paddingRight: fieldsValue.position.paddingRight,
      paddingBottom: fieldsValue.position.paddingBottom,
      paddingLeft: fieldsValue.position.paddingLeft,
      width: fieldsValue.position.width,
      height: fieldsValue.position.height,
      widthStyle: fieldsValue.position.widthStyle,
      heightStyle: fieldsValue.position.heightStyle,
      maxWidth: fieldsValue.position.maxWidth,
      maxHeight: fieldsValue.position.maxHeight,
      maxWidthStyle: fieldsValue.position.maxWidthStyle,
      maxHeightStyle: fieldsValue.position.maxHeightStyle,
      minWidth: fieldsValue.position.minWidth,
      minHeight: fieldsValue.position.minHeight,
      minWidthStyle: fieldsValue.position.minWidthStyle,
      minHeightStyle: fieldsValue.position.minHeightStyle,
      alignItems: fieldsValue.position.alignItems,
      textAlign: fieldsValue.position.textAlign,
      display: fieldsValue.position.display,
      xAxis: fieldsValue.position.xAxis,
      yAxis: fieldsValue.position.yAxis
    };

    stylingPositions = {
      ...stylingPositions,
      ...formValues
    };

    if (!project.stylingPosition) {
      project.stylingPosition = [];
    }
    project.stylingPosition.push(stylingPositions);

    const savedId = stylingPositions.positionId;

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.position?.positionIds && savedId) {
      updatedFieldsValue.position.positionIds.push(savedId);
      updatedFieldsValue.position.positionId = savedId;
      updatedFieldsValue.position.name = name;
      this.submitForm(updatedFieldsValue);
    }
  };

  edit = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const editDelegate = () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingPosition) {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        const positionBackup = _.clone(fieldsValue.position) as IStylingPosition;
        positionBackup.kuikaid = this.props.sd.selectedKuikaIds[0];
        this.props.sd.setCustomPositionStyleBackup(positionBackup);
        const stylingPositionDefList = this.props.sd.project.stylingPosition;
        const tempValue = stylingPositionDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          this.setPositionStyle(tempValue[0]);
        }
      }
    };
    if (mode === EditMode.custom) {
      editDelegate();
    } else {
      this.setPositionCustomStyle();
      editDelegate();
    }
  };

  duplicate = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const duplicateDelegate = async () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingPosition) {
        const stylingPositionDefList = this.props.sd.project.stylingPosition;
        const tempValue = stylingPositionDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          const copyValue = _.cloneDeep(tempValue[0]);
          copyValue.positionId = Guid.create().toString();
          copyValue.name = `${label}_copy`;
          copyValue.styleType = "custom";
          delete copyValue.overridden;

          let unique = false;
          while (!unique) {
            var found = false;
            this.props.sd.project.stylingPosition.forEach((stylingPosition) => {
              if (stylingPosition.name === copyValue.name) {
                copyValue.name += "_copy";
                found = true;
              }
            });
            unique = !found;
          }

          const project = _.cloneDeep(this.props.sd.project);
          project.stylingPosition.push(copyValue);
          const savedId = copyValue.positionId;
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.position?.positionIds && savedId) {
            fieldsValue.position.positionIds.push(savedId);
            this.submitForm(fieldsValue);
          }
        }
      }
    };
    if (mode === EditMode.custom) {
      duplicateDelegate();
    } else {
      this.setPositionCustomStyle();
      duplicateDelegate();
    }
  };

  removeFromComponent = (performTagOnClose: any) => {
    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  deleteFromProjectConfirmation = (label: string | undefined, performTagOnClose: any) => {
    if (label) {
      this.setState({
        deleteArguments: {
          deletingStyleName: label,
          isDeleteStyleModalVisible: true,
          performStyleTagOnClose: performTagOnClose
        }
      });
    }
  };

  isDefaultStying = (styingId: string) => {
    let found = false;
    this.props.sd.defaultStyleProvider?.getDesktopStyles().position.map((styling) => {
      if (styling.positionId?.toLowerCase() == styingId?.toLowerCase() || styling.name == styingId) found = true;
    });
    return found;
  };

  isCurrentComponentsDefaultStying = (styingId: string) => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());

    return (
      styingId?.toLowerCase() ==
      ScreenDesignHelper.getDefaultStylingForMobile(
        selectedDi!,
        "position",
        this.props.sd.defaultStyleProvider!
      )?.toLowerCase()
    );
  };

  deleteFromProject = async () => {
    const label = this.state.deleteArguments.deletingStyleName;

    if (!label) return;

    const currentId = this.props.sd.project.stylingPosition.find((item) => item.name === label)?.positionId;

    const found = this.isDefaultStying(currentId!);

    if (!found) {
      const mode: EditMode = this.getCurrentMode();
      const deleteDelegate = async () => {
        if (label && this.props?.sd?.project?.stylingPosition) {
          const project = _.cloneDeep(this.props.sd.project);
          project.stylingPosition = project.stylingPosition.filter((item) => item.name !== label);
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.position?.positionIds) {
            fieldsValue.position.positionIds = fieldsValue.position.positionIds.filter((id) => id !== currentId);
            this.submitForm(fieldsValue);
          }

          const { performStyleTagOnClose } = this.state.deleteArguments;
          if (performStyleTagOnClose) {
            performStyleTagOnClose();
          }

          this.setState({
            deleteArguments: {
              isDeleteStyleModalVisible: false,
              deletingStyleName: "",
              performStyleTagOnClose: undefined
            }
          });
        }
      };
      if (mode === EditMode.custom) {
        deleteDelegate();
      } else {
        this.setPositionCustomStyle();
        deleteDelegate();
      }
    } else {
      KMainFunctions.displayWarningNotification(
        "You cannot remove default styling from a project. If you wish you can remove it from this component"
      );
    }
  };

  getCurrentMode = () => {
    let mode: EditMode = EditMode.custom;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.position?.positionId && fieldsValue.position.positionId.length > 0) {
      if (fieldsValue.position.positionId === "-1") {
        mode = EditMode.create;
      } else {
        mode = EditMode.edit;
      }
    }
    return mode;
  };

  cancel = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position) {
      fieldsValue.position.positionId = null;
      fieldsValue.position.name = "";
      this.submitForm(fieldsValue);
    }
    this.setPositionCustomStyle();
  };

  save = async (name?: string, _mode?: EditMode) => {
    const mode: EditMode = _mode || this.getCurrentMode();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (!fieldsValue.position) {
      return;
    }

    if (name && name.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please write a name.");
      return;
    }

    if (mode === EditMode.create) {
      const isNameExist = this.props.sd.project.stylingPosition
        .map((s) => s.name)
        .includes(name || fieldsValue.position.name);
      if (isNameExist === true) {
        KMainFunctions.displayWarningNotification(
          "There cannot be another registration with the same name. Please write a different name."
        );
        return;
      }
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingPositions = {} as IStylingPosition;
    if (mode === EditMode.edit) {
      const data = project.stylingPosition;
      const selectedPosition = fieldsValue?.position?.positionId;
      if (selectedPosition && data && fieldsValue.position) {
        stylingPositions = data.filter((item) => item.positionId?.toLowerCase() === selectedPosition?.toLowerCase())[0];
      }
    }

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;

    if (!fieldsValue.position?.width || (fieldsValue.position?.width as any) === "") {
      delete fieldsValue.position.width;
    }
    if (!fieldsValue.position?.height || (fieldsValue.position?.height as any) === "") {
      delete fieldsValue.position.height;
    }

    if (mode === EditMode.create) {
      stylingPositions.positionId = Guid.create().toString();
    }
    stylingPositions.name = name || fieldsValue.position.name;
    stylingPositions.componentType = selectedDIItem?.name;

    const formValues = {
      isMarginLocked: fieldsValue.position.isMarginLocked,
      isPaddingLocked: fieldsValue.position.isPaddingLocked,
      marginTop: fieldsValue.position.marginTop,
      marginRight: fieldsValue.position.marginRight,
      marginBottom: fieldsValue.position.marginBottom,
      marginLeft: fieldsValue.position.marginLeft,
      paddingTop: fieldsValue.position.paddingTop,
      paddingRight: fieldsValue.position.paddingRight,
      paddingBottom: fieldsValue.position.paddingBottom,
      paddingLeft: fieldsValue.position.paddingLeft,
      width: fieldsValue.position.width,
      height: fieldsValue.position.height,
      widthStyle: fieldsValue.position.widthStyle,
      heightStyle: fieldsValue.position.heightStyle,
      maxWidth: fieldsValue.position.maxWidth,
      maxHeight: fieldsValue.position.maxHeight,
      maxWidthStyle: fieldsValue.position.maxWidthStyle,
      maxHeightStyle: fieldsValue.position.maxHeightStyle,
      minWidth: fieldsValue.position.minWidth,
      minHeight: fieldsValue.position.minHeight,
      minWidthStyle: fieldsValue.position.minWidthStyle,
      minHeightStyle: fieldsValue.position.minHeightStyle,
      alignItems: fieldsValue.position.alignItems,
      textAlign: fieldsValue.position.textAlign,
      display: fieldsValue.position.display,
      xAxis: fieldsValue.position.xAxis,
      yAxis: fieldsValue.position.yAxis
    };

    if (stylingPositions.styleType !== "custom" && mode === EditMode.edit) {
      const overridden = stylingPositions.overridden || [];

      Object.keys(formValues).forEach((key) => {
        if (!overridden.includes(key)) {
          if (formValues[key] !== stylingPositions[key]) {
            overridden.push(key);
          }
        }
      });

      stylingPositions.overridden = overridden;
    } else {
      stylingPositions.styleType = "custom";
      delete stylingPositions.overridden;
    }

    stylingPositions = {
      ...stylingPositions,
      ...formValues
    };

    if (!project.stylingPosition) {
      project.stylingPosition = [];
    }
    const exsistingPostionStyleIndex = project.stylingPosition.findIndex(
      (p) => p.positionId === stylingPositions.positionId
    );
    if (exsistingPostionStyleIndex >= 0) {
      project.stylingPosition[exsistingPostionStyleIndex] = stylingPositions;
    } else {
      project.stylingPosition.push(stylingPositions);
    }
    if (mode === EditMode.create) {
      const savedId = stylingPositions.positionId;
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.clearStylingPosition();
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.position?.positionIds && savedId) {
        fieldsValue.position.positionIds.push(savedId);
        this.submitForm(fieldsValue);
        this.setPositionCustomStyle();
      }
    } else {
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.setPositionCustomStyle();
    }
  };

  handleSelectedTagRemoved = (label: string) => {
    if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingPosition) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      const stylingPositionDefList = this.props.sd.project.stylingPosition;
      const tempValue = stylingPositionDefList.filter((item) => item.name === label || item.positionId === label);
      if (tempValue && tempValue.length > 0 && fieldsValue.position) {
        if (tempValue[0].positionId === fieldsValue.position.positionId) {
          this.setPositionCustomStyle();
        }
      }
    }
    return true;
  };

  handleChange = (value: string | number | Array<string | number> | null) => {
    if (!value || !Array.isArray(value) || value.length === 0) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.position) {
      fieldsValue.position.positionIds = value as string[];

      this.props.form.setFieldsValue(fieldsValue);

      this.props.form.submit();
    }
  };

  setPositionStyle = (val: IStylingPosition) => {
    if (val) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.position) {
        fieldsValue.position.positionId = val.positionId;
        fieldsValue.position.name = val.name;
        fieldsValue.position.isMarginLocked = val.isMarginLocked;
        fieldsValue.position.isPaddingLocked = val.isPaddingLocked;
        fieldsValue.position.marginTop = val.marginTop;
        fieldsValue.position.marginRight = val.marginRight;
        fieldsValue.position.marginBottom = val.marginBottom;
        fieldsValue.position.marginLeft = val.marginLeft;
        fieldsValue.position.paddingTop = val.paddingTop;
        fieldsValue.position.paddingRight = val.paddingRight;
        fieldsValue.position.paddingBottom = val.paddingBottom;
        fieldsValue.position.paddingLeft = val.paddingLeft;
        fieldsValue.position.width = val.width;
        fieldsValue.position.height = val.height;
        fieldsValue.position.widthStyle = val.widthStyle;
        fieldsValue.position.heightStyle = val.heightStyle;
        fieldsValue.position.maxWidth = val.maxWidth;
        fieldsValue.position.maxHeight = val.maxHeight;
        fieldsValue.position.maxWidthStyle = val.maxWidthStyle;
        fieldsValue.position.maxHeightStyle = val.maxHeightStyle;
        fieldsValue.position.minWidth = val.minWidth;
        fieldsValue.position.minHeight = val.minHeight;
        fieldsValue.position.minWidthStyle = val.minWidthStyle;
        fieldsValue.position.minHeightStyle = val.minHeightStyle;
        fieldsValue.position.componentType = val.componentType;
        fieldsValue.position.alignItems = val.alignItems;
        fieldsValue.position.textAlign = val.textAlign;
        fieldsValue.position.display = val.display;
        fieldsValue.position.xAxis = val.xAxis;
        fieldsValue.position.yAxis = val.yAxis;

        this.submitForm(fieldsValue);
      }
    }
  };

  setPositionCustomStyle = () => {
    const { backupCustomPosition } = this.props.sd;
    if (backupCustomPosition?.kuikaid === this.props.sd.selectedKuikaIds[0]) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.position && backupCustomPosition) {
        fieldsValue.position.positionId = null;
        fieldsValue.position.name = backupCustomPosition.name;
        fieldsValue.position.isMarginLocked =
          backupCustomPosition.isMarginLocked === undefined
            ? fieldsValue.position.isMarginLocked
            : backupCustomPosition.isMarginLocked;
        fieldsValue.position.isPaddingLocked =
          backupCustomPosition.isPaddingLocked === undefined
            ? fieldsValue.position.isPaddingLocked
            : backupCustomPosition.isPaddingLocked;
        fieldsValue.position.marginTop = backupCustomPosition.marginTop;
        fieldsValue.position.marginRight = backupCustomPosition.marginRight;
        fieldsValue.position.marginBottom = backupCustomPosition.marginBottom;
        fieldsValue.position.marginLeft = backupCustomPosition.marginLeft;
        fieldsValue.position.paddingTop = backupCustomPosition.paddingTop;
        fieldsValue.position.paddingRight = backupCustomPosition.paddingRight;
        fieldsValue.position.paddingBottom = backupCustomPosition.paddingBottom;
        fieldsValue.position.paddingLeft = backupCustomPosition.paddingLeft;
        fieldsValue.position.width = backupCustomPosition.width;
        fieldsValue.position.height = backupCustomPosition.height;
        fieldsValue.position.widthStyle =
          backupCustomPosition.widthStyle === undefined
            ? fieldsValue.position.widthStyle
            : backupCustomPosition.widthStyle;
        fieldsValue.position.heightStyle =
          backupCustomPosition.heightStyle === undefined
            ? fieldsValue.position.heightStyle
            : backupCustomPosition.heightStyle;
        fieldsValue.position.maxWidth = backupCustomPosition.maxWidth;
        fieldsValue.position.maxHeight = backupCustomPosition.maxHeight;
        fieldsValue.position.maxWidthStyle =
          backupCustomPosition.maxWidthStyle === undefined
            ? fieldsValue.position.maxWidthStyle
            : backupCustomPosition.maxWidthStyle;
        fieldsValue.position.maxHeightStyle =
          backupCustomPosition.maxHeightStyle === undefined
            ? fieldsValue.position.maxHeightStyle
            : backupCustomPosition.maxHeightStyle;
        fieldsValue.position.minWidth = backupCustomPosition.minWidth;
        fieldsValue.position.minHeight = backupCustomPosition.minHeight;
        fieldsValue.position.minWidthStyle =
          backupCustomPosition.minWidthStyle === undefined
            ? fieldsValue.position.minWidthStyle
            : backupCustomPosition.minWidthStyle;
        fieldsValue.position.minHeightStyle =
          backupCustomPosition.minHeightStyle === undefined
            ? fieldsValue.position.minHeightStyle
            : backupCustomPosition.minHeightStyle;
        fieldsValue.position.componentType = backupCustomPosition.componentType;
        fieldsValue.position.alignItems = backupCustomPosition.alignItems;
        fieldsValue.position.textAlign = backupCustomPosition.textAlign;
        fieldsValue.position.display = backupCustomPosition.display;
        fieldsValue.position.xAxis = backupCustomPosition.xAxis;
        fieldsValue.position.yAxis = backupCustomPosition.yAxis;

        this.clearProjectTempStyle();
        this.submitForm(fieldsValue);
      } else {
        this.clearStylingPosition();
      }
    } else {
      this.clearProjectTempStyle();
    }
  };

  clearProjectTempStyle = async () => {
    const { projectTempStyles } = this.props.sd;
    projectTempStyles.tempStylingPosition = undefined;
    this.props.sd.setProjectTempStyles(projectTempStyles);
    this.props.sd.setCustomPositionStyleBackup(undefined);
  };

  clearStylingPosition = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position) {
      fieldsValue.position.positionId = null;
      fieldsValue.position.name = undefined;
      fieldsValue.position.isMarginLocked = false;
      fieldsValue.position.isPaddingLocked = false;
      fieldsValue.position.marginTop = undefined;
      fieldsValue.position.marginRight = undefined;
      fieldsValue.position.marginBottom = undefined;
      fieldsValue.position.marginLeft = undefined;
      fieldsValue.position.paddingTop = undefined;
      fieldsValue.position.paddingRight = undefined;
      fieldsValue.position.paddingBottom = undefined;
      fieldsValue.position.paddingLeft = undefined;
      fieldsValue.position.width = undefined;
      fieldsValue.position.height = undefined;
      fieldsValue.position.widthStyle = "px";
      fieldsValue.position.heightStyle = "px";
      fieldsValue.position.maxWidth = undefined;
      fieldsValue.position.maxHeight = undefined;
      fieldsValue.position.maxWidthStyle = "px";
      fieldsValue.position.maxHeightStyle = "px";
      fieldsValue.position.minWidth = undefined;
      fieldsValue.position.minHeight = undefined;
      fieldsValue.position.minWidthStyle = "px";
      fieldsValue.position.minHeightStyle = "px";
      fieldsValue.position.alignItems = undefined;
      fieldsValue.position.textAlign = undefined;
      fieldsValue.position.display = undefined;
      fieldsValue.position.xAxis = undefined;
      fieldsValue.position.yAxis = undefined;
    }
    this.submitForm(fieldsValue);
  };

  componentDidUpdate = (prevProps: IPositionComponentProps & IWithSDProps, prevState: IPositionComponentState) => {
    const mode: EditMode = this.getCurrentMode();
    if (mode !== EditMode.custom && prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.clearProjectTempStyle();
      this.setPositionCustomStyle();
    }
    if (prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
    }
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  hasValue = (fieldPath: string[]) => {
    const value = this.props.form.getFieldValue(fieldPath);
    return value !== undefined && value !== null && value !== "";
  };

  getStyleClassNames = (fieldPaths: string[], baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    const hasAnyValue = fieldPaths.some((path) => this.hasValue(["position", path]));
    // KOA-15838: editing a style tag means the value IS a token value → blue
    // (`--has-value`), not the orange "changed" dot — even after a state switch
    // nulls the section id (which would flip getCurrentMode to custom).
    const isTokenValue: boolean = this.props.isEditMode === true || mode === EditMode.edit;

    return classNames(baseClassName, {
      "kuika-styling-panel__label--has-value": isTokenValue && hasAnyValue,
      "kuika-styling-panel__label--changed": !isTokenValue && hasAnyValue
    });
  };

  getPaddingInputClassNames = (direction: string) => {
    const mode: EditMode = this.getCurrentMode();
    const fieldPath = ["position", `padding${direction}`];
    const hasValue = this.hasValue(fieldPath);

    return classNames(`position__padding-input position__padding-input--${direction.toLowerCase()}`, {
      kuika_inputLocked: direction !== "Left" && this.getPaddingLocked() === true,
      "position__padding-input--has-value": mode === EditMode.edit && hasValue,
      "position__padding-input--changed":
        mode !== EditMode.edit && hasValue && !(this.getPaddingLocked() === true && direction !== "Left")
    });
  };

  getPaddingLabelClassNames = () => {
    const mode: EditMode = this.getCurrentMode();
    const hasAnyPaddingValue = ["Top", "Right", "Bottom", "Left"].some((dir) =>
      this.hasValue(["position", `padding${dir}`])
    );

    return classNames({
      "kuika-styling-panel__label--has-value": mode === EditMode.edit && hasAnyPaddingValue,
      "kuika-styling-panel__label--changed": mode !== EditMode.edit && hasAnyPaddingValue
    });
  };

  render() {
    const friendlyName = this.getFriendlyName();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const isReportDesigner: boolean = this.props.sd.dashboardState === DashboardState.reportDesigner;
    const isReportDesignPageSelected: boolean = this.props.sd.isReportDesignPageSelected();
    let isParentNotReportTableColumn: boolean = false;
    let isPanelAndParentReportTableColumn: boolean = false;
    let isParentReportTableColumnOrStaticTableColumn: boolean = false;
    let isReportTableColumnOrStaticTableColumn: boolean = false;
    const isScreenSelected = this.props.sd.selectedKuikaIds[0] === "-1";
    let selectedItem = {};
    if (selectedDi) {
      selectedItem = selectedDi!.item;
      const selectedDisParentDi = selectedDi.parentId
        ? this.props.sd.screenDesignDictionary[selectedDi.parentId]
        : undefined;
      isParentNotReportTableColumn =
        selectedDisParentDi?.item.name !== "ReportTableColumn" &&
        selectedDisParentDi?.item.name !== "StaticTableColumn";
      isParentReportTableColumnOrStaticTableColumn =
        (selectedDisParentDi?.item.name === "ReportTableColumn" ||
          selectedDisParentDi?.item.name === "StaticTableColumn") &&
        selectedDi?.item.name === "Label";
      if (
        selectedDi?.item.name === "Panel" &&
        (selectedDisParentDi?.item.name === "ReportTableColumn" ||
          selectedDisParentDi?.item.name === "StaticTableColumn")
      ) {
        isPanelAndParentReportTableColumn = true;
      }
      isReportTableColumnOrStaticTableColumn =
        selectedDi?.item.name === "ReportTable" ||
        selectedDi?.item.name === "ReportTableColumn" ||
        selectedDi?.item.name === "StaticTableColumn";
    }
    const isMergedWidth =
      selectedDi?.item?.name === "StaticTableColumn" &&
      StaticTableHelper.getExistingSpanValue(selectedDi, "colspan") > 1;
    const isMergedHeight =
      selectedDi?.item?.name === "StaticTableColumn" &&
      StaticTableHelper.getExistingSpanValue(selectedDi, "rowspan") > 1;

    const stylingVersionMenu = (label: string, performTagOnClose: any) => {
      const isDefaultStyling = this.isDefaultStying(label);
      const menu: any[] = [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => this.edit(label)
        },
        {
          key: "2",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicate(label)
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => this.removeFromComponent(performTagOnClose)
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteFromProjectConfirmation(label, performTagOnClose),
          danger: true,
          hidden: isDefaultStyling
        }
      ];
      return menu;
    };

    let mode: EditMode = this.getCurrentMode();

    const form = (
      <>
        <Form.Item name={["position", "positionId"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "textAlign"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "alignItems"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "display"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "componentType"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "isMarginLocked"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "isPaddingLocked"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "marginTop"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "marginRight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "marginBottom"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "marginLeft"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "paddingTop"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "paddingRight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "paddingBottom"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "paddingLeft"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "width"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "height"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "widthStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "heightStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "maxWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "maxHeight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "maxWidthStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "maxHeightStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "minWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "minHeight"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "minWidthStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "minHeightStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "xAxis"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "yAxis"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "positionIds"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["position", "name"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>

        <StylingSelector
          formItemName={["position", "positionIds"]}
          stylingList={this.props?.sd?.project?.stylingPosition}
          idField="positionId"
          handleSelectedTagRemoved={this.handleSelectedTagRemoved}
          isDefaultStyling={this.isDefaultStying}
          isCurrentComponentDefaultStyling={this.isCurrentComponentsDefaultStying}
          stylingVersionMenu={stylingVersionMenu}
          defaultStylingRegex={/KUIKA-[a-zA-Z]+-DEFAULT-POSITION(#[a-zA-Z]+#)*$/gm}
          new={this.new}
          cancel={this.cancel}
          save={this.save}
          edit={this.edit}
          mode={mode}
          form={this.props.form}
          onChange={this.handleChange}
          hide
        />
        <div
          className={classNames("kuika-styling-panel__position-form", {
            "kuika-styling-panel__editing": mode === EditMode.edit
          })}
        >
          <div className="kuika-styling-panel__form-field-wrapper kuika-styling-panel__form-field-wrapper--padding">
            <StyleLabel
              fieldPaths={["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"]}
              section="padding"
              hasValue={["Top", "Right", "Bottom", "Left"].some((dir) => this.hasValue(["position", `padding${dir}`]))}
              className={this.getPaddingLabelClassNames()}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Layout_Padding)}
            >
              Padding
            </StyleLabel>
            <div className="position__padding-area">
              <div className="position__padding-area--rectangle">
                <Form.Item
                  name={["position", "isPaddingLocked"]}
                  trigger="onLockChange"
                  getValueFromEvent={(locked): boolean => locked}
                  valuePropName="locked"
                  noStyle
                >
                  <StylingLockComponent
                    onLockChange={this.togglePaddingLocked}
                    className="position__padding-area--padding-lock"
                    disabled={this.isStyleHidden(InvisibleStylePart.Layout_Padding)}
                  ></StylingLockComponent>
                </Form.Item>
              </div>
              {isReportDesigner ? (
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const unit = this.getCurrentReportUnit();
                    const pxValue = this.props.form.getFieldValue(["position", "paddingTop"]);
                    const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                    return (
                      <KuikaInput
                        key={`paddingTop-${pxValue}-${unit}`}
                        precision={unit === "px" ? 0 : 3}
                        type="number"
                        size="sm"
                        defaultValue={displayValue}
                        onBlur={(e) => {
                          const inputStr = e.target.value.replace(",", ".");
                          const inputValue = parseFloat(inputStr);
                          const fieldsValue = this.props.form.getFieldsValue();
                          if (fieldsValue.position) {
                            if (!isNaN(inputValue)) {
                              const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                              fieldsValue.position.paddingTop = pxValue;
                            } else {
                              fieldsValue.position.paddingTop = undefined;
                            }
                            this.props.form.setFieldsValue(fieldsValue);
                            this.props.form.submit();
                          }
                        }}
                        placeholder="-"
                        disabled={
                          (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                          ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                            (isReportDesigner ? isParentNotReportTableColumn : true))
                        }
                        className={this.getPaddingInputClassNames("Top")}
                        min={0}
                        onKeyDown={(e) => {
                          if (e.key === "," || e.key === ".") {
                            return true;
                          }
                          return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                        }}
                        tabIndex={2}
                      />
                    );
                  }}
                </Form.Item>
              ) : (
                <Form.Item name={["position", "paddingTop"]} noStyle>
                  <KuikaInput
                    precision={0}
                    type="number"
                    placeholder="-"
                    disabled={
                      (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                      ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                        (isReportDesigner ? isParentNotReportTableColumn : true))
                    }
                    size="sm"
                    className={this.getPaddingInputClassNames("Top")}
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                    tabIndex={2}
                  />
                </Form.Item>
              )}
              {isReportDesigner ? (
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const unit = this.getCurrentReportUnit();
                    const pxValue = this.props.form.getFieldValue(["position", "paddingRight"]);
                    const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                    return (
                      <KuikaInput
                        key={`paddingRight-${pxValue}-${unit}`}
                        precision={unit === "px" ? 0 : 3}
                        type="number"
                        size="sm"
                        defaultValue={displayValue}
                        onBlur={(e) => {
                          const inputStr = e.target.value.replace(",", ".");
                          const inputValue = parseFloat(inputStr);
                          const fieldsValue = this.props.form.getFieldsValue();
                          if (fieldsValue.position) {
                            if (!isNaN(inputValue)) {
                              const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                              fieldsValue.position.paddingRight = pxValue;
                            } else {
                              fieldsValue.position.paddingRight = undefined;
                            }
                            this.props.form.setFieldsValue(fieldsValue);
                            this.props.form.submit();
                          }
                        }}
                        placeholder="-"
                        disabled={
                          (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                          ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                            (isReportDesigner ? isParentNotReportTableColumn : true))
                        }
                        className={this.getPaddingInputClassNames("Right")}
                        min={0}
                        onKeyDown={(e) => {
                          if (e.key === "," || e.key === ".") {
                            return true;
                          }
                          return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                        }}
                        tabIndex={3}
                      />
                    );
                  }}
                </Form.Item>
              ) : (
                <Form.Item name={["position", "paddingRight"]} noStyle>
                  <KuikaInput
                    precision={0}
                    size="sm"
                    disabled={
                      (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                      ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                        (isReportDesigner ? isParentNotReportTableColumn : true))
                    }
                    className={this.getPaddingInputClassNames("Right")}
                    type="number"
                    placeholder="-"
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                    tabIndex={3}
                  />
                </Form.Item>
              )}
              {isReportDesigner ? (
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const unit = this.getCurrentReportUnit();
                    const pxValue = this.props.form.getFieldValue(["position", "paddingBottom"]);
                    const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                    return (
                      <KuikaInput
                        key={`paddingBottom-${pxValue}-${unit}`}
                        precision={unit === "px" ? 0 : 3}
                        type="number"
                        size="sm"
                        defaultValue={displayValue}
                        onBlur={(e) => {
                          const inputStr = e.target.value.replace(",", ".");
                          const inputValue = parseFloat(inputStr);
                          const fieldsValue = this.props.form.getFieldsValue();
                          if (fieldsValue.position) {
                            if (!isNaN(inputValue)) {
                              const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                              fieldsValue.position.paddingBottom = pxValue;
                            } else {
                              fieldsValue.position.paddingBottom = undefined;
                            }
                            this.props.form.setFieldsValue(fieldsValue);
                            this.props.form.submit();
                          }
                        }}
                        placeholder="-"
                        disabled={
                          (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                          ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                            (isReportDesigner ? isParentNotReportTableColumn : true))
                        }
                        className={this.getPaddingInputClassNames("Bottom")}
                        min={0}
                        onKeyDown={(e) => {
                          if (e.key === "," || e.key === ".") {
                            return true;
                          }
                          return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                        }}
                        tabIndex={4}
                      />
                    );
                  }}
                </Form.Item>
              ) : (
                <Form.Item name={["position", "paddingBottom"]} noStyle>
                  <KuikaInput
                    precision={0}
                    size="sm"
                    disabled={
                      (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                      ((this.getPaddingLocked() || this.isStyleHidden(InvisibleStylePart.Layout_Padding)) &&
                        (isReportDesigner ? isParentNotReportTableColumn : true))
                    }
                    className={this.getPaddingInputClassNames("Bottom")}
                    type="number"
                    placeholder="-"
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                    tabIndex={4}
                  />
                </Form.Item>
              )}
              {isReportDesigner ? (
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const unit = this.getCurrentReportUnit();
                    const pxValue = this.props.form.getFieldValue(["position", "paddingLeft"]);
                    const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                    return (
                      <KuikaInput
                        key={`paddingLeft-${pxValue}-${unit}`}
                        precision={unit === "px" ? 0 : 3}
                        type="number"
                        size="sm"
                        defaultValue={displayValue}
                        onBlur={(e) => {
                          const inputStr = e.target.value.replace(",", ".");
                          const inputValue = parseFloat(inputStr);
                          const fieldsValue = this.props.form.getFieldsValue();
                          if (fieldsValue.position) {
                            if (!isNaN(inputValue)) {
                              const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                              fieldsValue.position.paddingLeft = pxValue;
                              if (this.getPaddingLocked()) {
                                fieldsValue.position.paddingTop = pxValue;
                                fieldsValue.position.paddingRight = pxValue;
                                fieldsValue.position.paddingBottom = pxValue;
                              }
                            } else {
                              fieldsValue.position.paddingLeft = undefined;
                              if (this.getPaddingLocked()) {
                                fieldsValue.position.paddingTop = undefined;
                                fieldsValue.position.paddingRight = undefined;
                                fieldsValue.position.paddingBottom = undefined;
                              }
                            }
                            this.props.form.setFieldsValue(fieldsValue);
                            this.props.form.submit();
                          }
                        }}
                        placeholder="-"
                        disabled={
                          (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                          (this.isStyleHidden(InvisibleStylePart.Layout_Padding) &&
                            (isReportDesigner ? isParentNotReportTableColumn : true))
                        }
                        className={this.getPaddingInputClassNames("Left")}
                        min={0}
                        onKeyDown={(e) => {
                          if (e.key === "," || e.key === ".") {
                            return true;
                          }
                          return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                        }}
                        tabIndex={1}
                      />
                    );
                  }}
                </Form.Item>
              ) : (
                <Form.Item name={["position", "paddingLeft"]} noStyle>
                  <KuikaInput
                    precision={0}
                    size="sm"
                    disabled={
                      (isReportDesignPageSelected && !isReportTableColumnOrStaticTableColumn) ||
                      (this.isStyleHidden(InvisibleStylePart.Layout_Padding) &&
                        (isReportDesigner ? isParentNotReportTableColumn : true))
                    }
                    className={this.getPaddingInputClassNames("Left")}
                    type="number"
                    placeholder="-"
                    min={0}
                    onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                    tabIndex={1}
                  />
                </Form.Item>
              )}
            </div>
          </div>

          <div
            className="kuika-styling-panel__form-field-wrapper"
            style={{
              display: isReportDesigner && selectedDi?.id === -1 ? "none" : "flex"
            }}
          >
            <StyleLabel
              fieldPaths={["alignItems", "textAlign"]}
              section="align"
              hasValue={this.hasValue(["position", "alignItems"]) || this.hasValue(["position", "textAlign"])}
              className={this.getStyleClassNames(["alignItems", "textAlign"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Layout_AlignItems)}
            >
              Align
            </StyleLabel>
            <div className="kuika-flex-between kuika-width-full">
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Left" position="top">
                      <KuikaButton
                        onClick={() => this.toggleTextAlign(TextAlignTypes.Left)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_TextAlign)}
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "textAlign"]) !== TextAlignTypes.Left,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "textAlign"]) === TextAlignTypes.Left
                        })}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        tabIndex={5}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-left" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Center" position="top">
                      <KuikaButton
                        onClick={() => this.toggleTextAlign(TextAlignTypes.Center)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_TextAlign)}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "textAlign"]) !== TextAlignTypes.Center,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "textAlign"]) === TextAlignTypes.Center
                        })}
                        tabIndex={6}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-center" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Right" position="top">
                      <KuikaButton
                        onClick={() => this.toggleTextAlign(TextAlignTypes.Right)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_TextAlign)}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "textAlign"]) !== TextAlignTypes.Right,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "textAlign"]) === TextAlignTypes.Right
                        })}
                        tabIndex={7}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-right" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Top" position="top">
                      <KuikaButton
                        onClick={() => this.toggleAlignItems(AlignItemsTypes.FlexStart)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_AlignItems)}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "alignItems"]) !== AlignItemsTypes.FlexStart,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "alignItems"]) === AlignItemsTypes.FlexStart
                        })}
                        tabIndex={8}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-top" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Middle" position="top">
                      <KuikaButton
                        onClick={() => this.toggleAlignItems(AlignItemsTypes.Center)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_AlignItems)}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "alignItems"]) !== AlignItemsTypes.Center,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "alignItems"]) === AlignItemsTypes.Center
                        })}
                        tabIndex={9}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-middle" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  return (
                    <KuikaTooltip content="Align Bottom" position="top">
                      <KuikaButton
                        onClick={() => this.toggleAlignItems(AlignItemsTypes.FlexEnd)}
                        disabled={this.isStyleHidden(InvisibleStylePart.Layout_AlignItems)}
                        isIcon
                        color="neutral"
                        type="tertiary"
                        size="sm"
                        className={classNames({
                          "kuika-styling-panel__button":
                            this.props.form.getFieldValue(["position", "alignItems"]) !== AlignItemsTypes.FlexEnd,
                          "kuika-styling-panel__button--selected":
                            this.props.form.getFieldValue(["position", "alignItems"]) === AlignItemsTypes.FlexEnd
                        })}
                        tabIndex={10}
                      >
                        <KuikaSvg width={16} height={16} name="layout-align-bottom" type="styling-panel-icons" />
                      </KuikaButton>
                    </KuikaTooltip>
                  );
                }}
              </Form.Item>
            </div>
          </div>
          <div
            className="kuika-styling-panel__position-size-wrapper"
            style={{
              display: isReportDesigner && selectedDi?.id === -1 ? "none" : "flex"
            }}
          >
            <div className="kuika-styling-panel__position-size-wrapper--child">
              <div className="kuika-styling-panel__position-size-wrapper--child--item">
                <StyleLabel
                  fieldPaths={["width", "widthStyle"]}
                  section="width"
                  hasValue={this.hasValue(["position", "width"])}
                  className={this.getStyleClassNames(["width"])}
                  onReset={this.resetSectionValues}
                  disabled={
                    isMergedWidth ||
                    (isReportDesigner && isParentReportTableColumnOrStaticTableColumn) ||
                    ((!!(
                      KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                      this.props.sd.selectedKuikaIds[0] === "-1"
                    ) ||
                      this.isStyleHidden(InvisibleStylePart.Layout_Width)) &&
                      !isPanelAndParentReportTableColumn)
                  }
                >
                  Width
                </StyleLabel>
                <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                  {isReportDesigner ? (
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const pxValue = this.props.form.getFieldValue(["position", "width"]);
                        const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                        return (
                          <KuikaInput
                            key={`width-${pxValue}-${unit}`}
                            precision={unit === "px" ? 0 : 3}
                            type="number"
                            size="sm"
                            defaultValue={displayValue}
                            onBlur={(e) => {
                              const inputStr = e.target.value.replace(",", ".");
                              const inputValue = parseFloat(inputStr);
                              const fieldsValue = this.props.form.getFieldsValue();
                              if (fieldsValue.position) {
                                if (!isNaN(inputValue)) {
                                  const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                                  fieldsValue.position.width = pxValue;
                                } else {
                                  fieldsValue.position.width = undefined;
                                }
                                this.props.form.setFieldsValue(fieldsValue);
                                this.props.form.submit();
                              }
                            }}
                            disabled={
                              isMergedWidth ||
                              (isReportDesigner && isParentReportTableColumnOrStaticTableColumn) ||
                              ((!!(
                                KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                                this.props.sd.selectedKuikaIds[0] === "-1"
                              ) ||
                                this.isStyleHidden(InvisibleStylePart.Layout_Width)) &&
                                !isPanelAndParentReportTableColumn)
                            }
                            placeholder="-"
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === ".") {
                                return true;
                              }
                              return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                            }}
                            min={0}
                            tabIndex={11}
                          />
                        );
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item name={["position", "width"]} noStyle>
                      <KuikaInput
                        precision={0}
                        type="number"
                        size="sm"
                        disabled={
                          isMergedWidth ||
                          (isReportDesigner && isParentReportTableColumnOrStaticTableColumn) ||
                          ((!!(
                            KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                            this.props.sd.selectedKuikaIds[0] === "-1"
                          ) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_Width)) &&
                            !isPanelAndParentReportTableColumn)
                        }
                        placeholder="-"
                        onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                        min={0}
                        tabIndex={11}
                      />
                    </Form.Item>
                  )}
                  {isReportDesigner ? (
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const displayLabel = unit === "inch" ? "in" : unit;

                        return (
                          <KuikaSelectbox
                            disabled={true}
                            size="sm"
                            value={unit}
                            defaultValue="px"
                            suffixIcon={<></>}
                            options={[
                              {
                                label: displayLabel,
                                value: unit
                              }
                            ]}
                          />
                        );
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item name={["position", "widthStyle"]} noStyle>
                      <KuikaSelectbox
                        disabled={
                          isMergedWidth ||
                          (isReportDesigner && isParentReportTableColumnOrStaticTableColumn) ||
                          !!(
                            KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                            this.props.sd.selectedKuikaIds[0] === "-1"
                          ) ||
                          this.isStyleHidden(InvisibleStylePart.Layout_Width)
                        }
                        size="sm"
                        defaultValue="px"
                        suffixIcon={<></>}
                        options={(() => {
                          const opts = [
                            {
                              label: "px",
                              value: "px"
                            },
                            {
                              label: "%",
                              value: "%"
                            }
                          ];

                          if (this.props.sd.project.projectType === EProjectType.Desktop) {
                            opts.push({
                              label: "vw",
                              value: "vw"
                            });
                          }

                          return opts;
                        })()}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
              <div className="kuika-styling-panel__position-size-wrapper--child--item">
                <StyleLabel
                  fieldPaths={["height", "heightStyle"]}
                  section="height"
                  hasValue={this.hasValue(["position", "height"])}
                  className={this.getStyleClassNames(["height"])}
                  onReset={this.resetSectionValues}
                  disabled={
                    isMergedHeight ||
                    !!(
                      KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                      this.props.sd.selectedKuikaIds[0] === "-1"
                    ) ||
                    this.isStyleHidden(InvisibleStylePart.Layout_Height)
                  }
                >
                  Height
                </StyleLabel>
                <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                  {isReportDesigner ? (
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const pxValue = this.props.form.getFieldValue(["position", "height"]);
                        const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                        return (
                          <KuikaInput
                            key={`height-${pxValue}-${unit}`}
                            precision={unit === "px" ? 0 : 3}
                            type="number"
                            size="sm"
                            defaultValue={displayValue}
                            onBlur={(e) => {
                              const inputStr = e.target.value.replace(",", ".");
                              const inputValue = parseFloat(inputStr);
                              const fieldsValue = this.props.form.getFieldsValue();
                              if (fieldsValue.position) {
                                if (!isNaN(inputValue)) {
                                  const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                                  fieldsValue.position.height = pxValue;
                                } else {
                                  fieldsValue.position.height = undefined;
                                }
                                this.props.form.setFieldsValue(fieldsValue);
                                this.props.form.submit();
                              }
                            }}
                            disabled={
                              isMergedHeight ||
                              !!(
                                KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                                this.props.sd.selectedKuikaIds[0] === "-1"
                              ) ||
                              this.isStyleHidden(InvisibleStylePart.Layout_Height)
                            }
                            placeholder="-"
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === ".") {
                                return true;
                              }
                              return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                            }}
                            min={0}
                            tabIndex={12}
                          />
                        );
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item name={["position", "height"]} noStyle>
                      <KuikaInput
                        precision={0}
                        type="number"
                        size="sm"
                        disabled={
                          isMergedHeight ||
                          !!(
                            KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                            this.props.sd.selectedKuikaIds[0] === "-1"
                          ) ||
                          this.isStyleHidden(InvisibleStylePart.Layout_Height)
                        }
                        placeholder="-"
                        onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                        min={0}
                        tabIndex={12}
                      />
                    </Form.Item>
                  )}
                  {isReportDesigner ? (
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const displayLabel = unit === "inch" ? "in" : unit;

                        return (
                          <KuikaSelectbox
                            disabled={true}
                            size="sm"
                            value={unit}
                            defaultValue="px"
                            suffixIcon={<></>}
                            options={[
                              {
                                label: displayLabel,
                                value: unit
                              }
                            ]}
                          />
                        );
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item name={["position", "heightStyle"]} noStyle>
                      <KuikaSelectbox
                        disabled={
                          isMergedHeight ||
                          !!(
                            KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                            this.props.sd.selectedKuikaIds[0] === "-1"
                          ) ||
                          this.isStyleHidden(InvisibleStylePart.Layout_Height)
                        }
                        size="sm"
                        defaultValue="px"
                        suffixIcon={<></>}
                        options={(() => {
                          const opts = [
                            {
                              label: "px",
                              value: "px"
                            },
                            {
                              label: "%",
                              value: "%"
                            }
                          ];

                          if (this.props.sd.project.projectType === EProjectType.Desktop) {
                            opts.push({
                              label: "vh",
                              value: "vh"
                            });
                          }

                          return opts;
                        })()}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            </div>
            {!isReportDesigner && (
              <>
                <div className="kuika-styling-panel__position-size-wrapper--child">
                  <div className="kuika-styling-panel__position-size-wrapper--child--item">
                    <StyleLabel
                      fieldPaths={["minWidth", "minWidthStyle"]}
                      section="min width"
                      hasValue={this.hasValue(["position", "minWidth"])}
                      className={this.getStyleClassNames(["minWidth"])}
                      onReset={this.resetSectionValues}
                      disabled={
                        !!(
                          KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                          this.props.sd.selectedKuikaIds[0] === "-1"
                        ) ||
                        this.isStyleHidden(InvisibleStylePart.Layout_MinWidth) ||
                        this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                      }
                    >
                      Min W
                    </StyleLabel>
                    <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                      <Form.Item name={["position", "minWidth"]} noStyle>
                        <KuikaInput
                          precision={0}
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MinWidth) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                          }
                          type="number"
                          size="sm"
                          placeholder="-"
                          onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                          min={0}
                          tabIndex={13}
                        />
                      </Form.Item>
                      <Form.Item name={["position", "minWidthStyle"]} noStyle>
                        <KuikaSelectbox
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MinWidth) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                          }
                          size="sm"
                          defaultValue="px"
                          suffixIcon={<></>}
                          options={(() => {
                            const opts = [
                              {
                                label: "px",
                                value: "px"
                              },
                              {
                                label: "%",
                                value: "%"
                              }
                            ];

                            if (this.props.sd.project.projectType === EProjectType.Desktop) {
                              opts.push({
                                label: "vw",
                                value: "vw"
                              });
                            }

                            return opts;
                          })()}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="kuika-styling-panel__position-size-wrapper--child--item">
                    <StyleLabel
                      fieldPaths={["minHeight", "minHeightStyle"]}
                      section="min height"
                      hasValue={this.hasValue(["position", "minHeight"])}
                      className={this.getStyleClassNames(["minHeight"])}
                      onReset={this.resetSectionValues}
                      disabled={
                        !!(
                          KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                          this.props.sd.selectedKuikaIds[0] === "-1"
                        ) ||
                        this.isStyleHidden(InvisibleStylePart.Layout_MinHeight) ||
                        this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                      }
                    >
                      Min H
                    </StyleLabel>
                    <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                      <Form.Item name={["position", "minHeight"]} noStyle>
                        <KuikaInput
                          precision={0}
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MinHeight) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                          }
                          type="number"
                          size="sm"
                          placeholder="-"
                          onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                          min={0}
                          tabIndex={14}
                        />
                      </Form.Item>
                      <Form.Item name={["position", "minHeightStyle"]} noStyle>
                        <KuikaSelectbox
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MinHeight) ||
                            this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                          }
                          size="sm"
                          defaultValue="px"
                          suffixIcon={<></>}
                          options={(() => {
                            const opts = [
                              {
                                label: "px",
                                value: "px"
                              },
                              {
                                label: "%",
                                value: "%"
                              }
                            ];

                            if (this.props.sd.project.projectType === EProjectType.Desktop) {
                              opts.push({
                                label: "vh",
                                value: "vh"
                              });
                            }

                            return opts;
                          })()}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
                <div className="kuika-styling-panel__position-size-wrapper--child">
                  <div className="kuika-styling-panel__position-size-wrapper--child--item">
                    <StyleLabel
                      fieldPaths={["maxWidth", "maxWidthStyle"]}
                      section="max width"
                      hasValue={this.hasValue(["position", "maxWidth"])}
                      className={this.getStyleClassNames(["maxWidth"])}
                      onReset={this.resetSectionValues}
                      disabled={
                        !!(
                          KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                          this.props.sd.selectedKuikaIds[0] === "-1"
                        ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                      }
                    >
                      Max W
                    </StyleLabel>
                    <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                      <Form.Item name={["position", "maxWidth"]} noStyle>
                        <KuikaInput
                          precision={0}
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                          }
                          type="number"
                          size="sm"
                          placeholder="-"
                          onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                          min={0}
                          tabIndex={15}
                        />
                      </Form.Item>
                      <Form.Item name={["position", "maxWidthStyle"]} noStyle>
                        <KuikaSelectbox
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxWidth)
                          }
                          size="sm"
                          defaultValue="px"
                          suffixIcon={<></>}
                          options={(() => {
                            const opts = [
                              {
                                label: "px",
                                value: "px"
                              },
                              {
                                label: "%",
                                value: "%"
                              }
                            ];

                            if (this.props.sd.project.projectType === EProjectType.Desktop) {
                              opts.push({
                                label: "vw",
                                value: "vw"
                              });
                            }

                            return opts;
                          })()}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="kuika-styling-panel__position-size-wrapper--child--item">
                    <StyleLabel
                      fieldPaths={["maxHeight", "maxHeightStyle"]}
                      section="max height"
                      hasValue={this.hasValue(["position", "maxHeight"])}
                      className={this.getStyleClassNames(["maxHeight"])}
                      onReset={this.resetSectionValues}
                      disabled={
                        !!(
                          KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                          this.props.sd.selectedKuikaIds[0] === "-1"
                        ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                      }
                    >
                      Max H
                    </StyleLabel>
                    <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                      <Form.Item name={["position", "maxHeight"]} noStyle>
                        <KuikaInput
                          precision={0}
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                          }
                          type="number"
                          size="sm"
                          placeholder="-"
                          onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                          min={0}
                          tabIndex={16}
                        />
                      </Form.Item>
                      <Form.Item name={["position", "maxHeightStyle"]} noStyle>
                        <KuikaSelectbox
                          disabled={
                            !!(
                              KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                              this.props.sd.selectedKuikaIds[0] === "-1"
                            ) || this.isStyleHidden(InvisibleStylePart.Layout_MaxHeight)
                          }
                          size="sm"
                          defaultValue="px"
                          suffixIcon={<></>}
                          options={(() => {
                            const opts = [
                              {
                                label: "px",
                                value: "px"
                              },
                              {
                                label: "%",
                                value: "%"
                              }
                            ];

                            if (this.props.sd.project.projectType === EProjectType.Desktop) {
                              opts.push({
                                label: "vh",
                                value: "vh"
                              });
                            }

                            return opts;
                          })()}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </>
            )}
            {this.props.sd.dashboardState === DashboardState.reportDesigner && (
              <div className="kuika-styling-panel__position-size-wrapper--child">
                <div className="kuika-styling-panel__position-size-wrapper--child--item">
                  <StyleLabel
                    fieldPaths={["xAxis"]}
                    section="X axis"
                    hasValue={this.hasValue(["position", "xAxis"])}
                    className={this.getStyleClassNames(["xAxis"])}
                    onReset={this.resetSectionValues}
                    disabled={
                      !!(
                        KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                        this.props.sd.selectedKuikaIds[0] === "-1"
                      ) || this.isStyleHidden(InvisibleStylePart.Layout_XAxis)
                    }
                  >
                    X
                  </StyleLabel>
                  <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const pxValue = this.props.form.getFieldValue(["position", "xAxis"]);
                        const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                        return (
                          <KuikaInput
                            key={`xAxis-${pxValue}-${unit}`}
                            precision={unit === "px" ? 0 : 3}
                            type="number"
                            size="sm"
                            defaultValue={displayValue}
                            onBlur={(e) => {
                              const inputStr = e.target.value.replace(",", ".");
                              const inputValue = parseFloat(inputStr);
                              const fieldsValue = this.props.form.getFieldsValue();
                              if (fieldsValue.position) {
                                if (!isNaN(inputValue)) {
                                  const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                                  fieldsValue.position.xAxis = pxValue;
                                } else {
                                  fieldsValue.position.xAxis = undefined;
                                }
                                this.props.form.setFieldsValue(fieldsValue);
                                this.props.form.submit();
                              }
                            }}
                            disabled={
                              !!(
                                KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                                this.props.sd.selectedKuikaIds[0] === "-1"
                              ) || this.isStyleHidden(InvisibleStylePart.Layout_XAxis)
                            }
                            placeholder="-"
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === ".") {
                                return true;
                              }
                              return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                            }}
                            tabIndex={17}
                          />
                        );
                      }}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const displayLabel = unit === "inch" ? "in" : unit;

                        return (
                          <KuikaSelectbox
                            disabled={true}
                            size="sm"
                            value={unit}
                            defaultValue="px"
                            suffixIcon={<></>}
                            options={[
                              {
                                label: displayLabel,
                                value: unit
                              }
                            ]}
                          />
                        );
                      }}
                    </Form.Item>
                  </div>
                </div>
                <div className="kuika-styling-panel__position-size-wrapper--child--item">
                  <StyleLabel
                    fieldPaths={["yAxis"]}
                    section="Y axis"
                    hasValue={this.hasValue(["position", "yAxis"])}
                    className={this.getStyleClassNames(["yAxis"])}
                    onReset={this.resetSectionValues}
                    disabled={
                      !!(
                        KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                        this.props.sd.selectedKuikaIds[0] === "-1"
                      ) || this.isStyleHidden(InvisibleStylePart.Layout_YAxis)
                    }
                  >
                    Y
                  </StyleLabel>
                  <div className="kuika-styling-panel__double-input-wrapper kuika-styling-panel__input-wrapper">
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const pxValue = this.props.form.getFieldValue(["position", "yAxis"]);
                        const displayValue = this.convertPxToDisplayUnit(pxValue, unit);

                        return (
                          <KuikaInput
                            key={`yAxis-${pxValue}-${unit}`}
                            precision={unit === "px" ? 0 : 3}
                            type="number"
                            size="sm"
                            defaultValue={displayValue}
                            onBlur={(e) => {
                              const inputStr = e.target.value.replace(",", ".");
                              const inputValue = parseFloat(inputStr);
                              const fieldsValue = this.props.form.getFieldsValue();
                              if (fieldsValue.position) {
                                if (!isNaN(inputValue)) {
                                  const pxValue = this.convertDisplayUnitToPx(inputValue, unit);
                                  fieldsValue.position.yAxis = pxValue;
                                } else {
                                  fieldsValue.position.yAxis = undefined;
                                }
                                this.props.form.setFieldsValue(fieldsValue);
                                this.props.form.submit();
                              }
                            }}
                            disabled={
                              !!(
                                KMainFunctions.isArrHasValue(this.props.sd.selectedKuikaIds) &&
                                this.props.sd.selectedKuikaIds[0] === "-1"
                              ) || this.isStyleHidden(InvisibleStylePart.Layout_YAxis)
                            }
                            placeholder="-"
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === ".") {
                                return true;
                              }
                              return KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault();
                            }}
                            tabIndex={18}
                          />
                        );
                      }}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const unit = this.getCurrentReportUnit();
                        const displayLabel = unit === "inch" ? "in" : unit;

                        return (
                          <KuikaSelectbox
                            disabled={true}
                            size="sm"
                            value={unit}
                            defaultValue="px"
                            suffixIcon={<></>}
                            options={[
                              {
                                label: displayLabel,
                                value: unit
                              }
                            ]}
                          />
                        );
                      }}
                    </Form.Item>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="kuika-styling-panel__form-field-wrapper kuika-styling-panel__position-display-wrapper"
            style={{
              display:
                this.isStyleHidden(InvisibleStylePart.Layout_Display) || (isReportDesigner && selectedDi?.id === -1)
                  ? "none"
                  : "flex"
            }}
          >
            <StyleLabel
              fieldPaths={["display"]}
              section="display"
              hasValue={this.hasValue(["position", "display"])}
              className={this.getStyleClassNames(["display"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Layout_Display)}
            >
              Display
            </StyleLabel>
            <KuikaTabs
              options={[
                {
                  value: "Inline",
                  icon: (
                    <div className="kuika-flex-center--gap-xs">
                      <KuikaSvg name="inline" type="styling-panel-icons" width={12} height={12} />
                      <KuikaSmallMediumText className="kuika-styling-panel__position-display-inline-text">
                        Inline
                      </KuikaSmallMediumText>
                    </div>
                  )
                },
                {
                  value: "Block",
                  icon: (
                    <div className="kuika-flex-center--gap-xs">
                      <KuikaSvg name="block" type="styling-panel-icons" width={12} height={12} />
                      <KuikaSmallMediumText className="kuika-styling-panel__position-display-inline-text">
                        Block
                      </KuikaSmallMediumText>
                    </div>
                  )
                }
              ]}
              onChange={(value) => this.toggleDisplay(value === "Inline" ? DisplayTypes.Inline : DisplayTypes.Block)}
              value={(() => {
                const displayValue = this.props.form.getFieldValue(["position", "display"]);
                if (displayValue === DisplayTypes.Block) return "Block";
                if (displayValue === DisplayTypes.Inline) return "Inline";
                // Default to Block for Widget Renderer, otherwise Inline
                return selectedDi?.item?.name === "Widget Renderer" ? "Block" : "Inline";
              })()}
              className="kuika-styling-panel__tabs--full"
            />
          </div>
        </div>
      </>
    );

    const deleteModal = (
      <>
        <DeleteModal
          itemName={this.state.deleteArguments.deletingStyleName}
          triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
          deleteItem={() => this.deleteFromProject()}
          deleteType={"Style"}
          closeModal={() =>
            this.setState({
              deleteArguments: {
                isDeleteStyleModalVisible: false,
                deletingStyleName: "",
                performStyleTagOnClose: undefined
              }
            })
          }
        />
      </>
    );
    return (
      <>
        {deleteModal}
        {form}
      </>
    );
  }

  getMarginLocked = () => {
    const res = this.props.form.getFieldValue(["position", "isMarginLocked"]) === true;
    return res;
  };

  toggleMarginLocked = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position?.isMarginLocked === true) {
      fieldsValue.position.isMarginLocked = false;
    } else if (fieldsValue.position) {
      fieldsValue.position.isMarginLocked = true;
      fieldsValue.position.marginRight = fieldsValue.position.marginTop;
      fieldsValue.position.marginBottom = fieldsValue.position.marginTop;
      fieldsValue.position.marginLeft = fieldsValue.position.marginTop;
    }
    this.submitForm(fieldsValue);
  };

  getPaddingLocked = () => {
    const res = this.props.form.getFieldValue(["position", "isPaddingLocked"]) === true;
    return res;
  };

  togglePaddingLocked = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.position?.isPaddingLocked) {
      fieldsValue.position.paddingTop = fieldsValue.position.paddingLeft || undefined;
      fieldsValue.position.paddingRight = fieldsValue.position.paddingLeft || undefined;
      fieldsValue.position.paddingBottom = fieldsValue.position.paddingLeft || undefined;
    }
    this.submitForm(fieldsValue);
  };

  toggleTextAlign = (value: TextAlignTypes) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position?.textAlign === value) {
      fieldsValue.position.textAlign = undefined;
    } else if (fieldsValue.position) {
      fieldsValue.position.textAlign = value;
    }
    this.submitForm(fieldsValue);
  };

  toggleAlignItems = (value: AlignItemsTypes) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position?.alignItems === value) {
      fieldsValue.position.alignItems = undefined;
    } else if (fieldsValue.position) {
      fieldsValue.position.alignItems = value;
    }
    this.submitForm(fieldsValue);
  };

  toggleDisplay = (value: DisplayTypes) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.position?.display === value) {
      fieldsValue.position.display = undefined;
    } else if (fieldsValue.position) {
      fieldsValue.position.display = value;
      fieldsValue.position.alignItems = undefined;
      fieldsValue.position.textAlign = undefined;
    }

    this.submitForm(fieldsValue);
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  resetSectionValues = (fieldPaths: string[]) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    fieldPaths.forEach((path) => {
      if (fieldsValue.position && fieldsValue.position[path]) {
        fieldsValue.position[path] = undefined;
      }
    });
    this.submitForm(fieldsValue);
  };
}

const positionComponent = withContext<IPositionComponentProps>(withSD<IPositionComponentProps>(PositionComponent));
export { positionComponent as PositionComponent };
